/**
 * Feedback / bug reporting against the platform's house bug-report module —
 * the same `turbotwin-bug-report` database the TurboTwin webclient, the
 * Unreal client and the Twin Builder studio submit to, so portal reports
 * land in the one triage queue. Lazy socket: opens the first time the
 * dialog does (services/bugReport.ts).
 */
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ChangeEvent,
  type CSSProperties,
  type FormEvent,
} from 'react';
import { useAuthKit } from '../../contexts/AuthProvider';
import { useOrgSession } from '../../contexts/OrgContext';
import { useProjects } from '../../hooks/useProjects';
import { useTweaks } from '../../hooks/useTweaks';
import {
  bugReportConnectionView,
  connectBugReport,
  reportBugReportTokenFailure,
  retryBugReportConnection,
  submitBugReport,
  uploadReportExtras,
  uploadReportImages,
  validateImageFile,
  useBugReportState,
  IMAGE_ID_WAIT_MS,
  MAX_REPORT_IMAGES,
  type ReportKindTag,
} from '../../services/bugReport';
import { captureTabScreenshot, getSessionLogText } from 'turbotwin-sdk/bug-report';
import { firstReason } from '@/lib/disabledReason';
import { GatedButton } from '@/components/GatedButton';
import { Icons } from 'turbotwin-sdk/icons';
import { Icon } from '@/components/Icon';

const STDB_URI = import.meta.env.VITE_STDB_CLOUD_URI as string;

/**
 * How long to wait for AuthKit before giving up on this attempt. Its refresh
 * fetch has no timeout of its own, and the socket's connect timeout only
 * starts once a token is in hand.
 */
const TOKEN_TIMEOUT_MS = 8_000;

function withTimeout<T>(promise: Promise<T>, ms: number, message: string): Promise<T> {
  let timer: ReturnType<typeof setTimeout> | undefined;
  const timeout = new Promise<never>((_, reject) => {
    timer = setTimeout(() => reject(new Error(message)), ms);
  });
  return Promise.race([promise, timeout]).finally(() => clearTimeout(timer));
}

const KIND_OPTIONS: Array<{ tag: ReportKindTag; label: string }> = [
  { tag: 'Bug', label: 'Bug' },
  { tag: 'FeatureRequest', label: 'Feature request' },
  { tag: 'Question', label: 'Question' },
  { tag: 'Feedback', label: 'Feedback' },
];

function formatBytes(n: number): string {
  if (n >= 1024 * 1024) return `${(n / (1024 * 1024)).toFixed(1)} MB`;
  if (n >= 1024) return `${Math.round(n / 1024)} KB`;
  return `${n} B`;
}

const FIELD_STYLE: CSSProperties = {
  width: '100%',
  boxSizing: 'border-box',
  background: 'var(--panel)',
  border: '1px solid var(--border-2)',
  borderRadius: 'var(--r-sm, 6px)',
  color: 'var(--text)',
  padding: '8px 10px',
  font: '400 13px var(--sans)',
};

const LABEL_STYLE: CSSProperties = {
  display: 'block',
  font: '600 11px var(--sans)',
  letterSpacing: '0.04em',
  textTransform: 'uppercase',
  color: 'var(--muted)',
  marginBottom: 6,
};

const BTN_STYLE: CSSProperties = {
  border: '1px solid var(--border-2)',
  background: 'var(--panel-2)',
  color: 'var(--text)',
  borderRadius: 'var(--r-sm, 6px)',
  padding: '7px 14px',
  font: '600 12.5px var(--sans)',
  cursor: 'pointer',
};

export function BugReportDialog({ isOpen, onClose }: { isOpen: boolean; onClose: () => void }) {
  const { user, getAccessToken } = useAuthKit();
  const { org } = useOrgSession();
  const { sites } = useProjects();
  const { tweaks } = useTweaks();
  const { status, lastError } = useBugReportState();

  const [kind, setKind] = useState<ReportKindTag>('Bug');
  const [summary, setSummary] = useState('');
  const [description, setDescription] = useState('');
  const [pendingFiles, setPendingFiles] = useState<File[]>([]);
  const [fileError, setFileError] = useState<string | null>(null);
  // Tab capture defaults OFF (it forces the browser's share-this-tab prompt);
  // the session log defaults ON. Same defaults as the platform webclient.
  const [includeScreenshot, setIncludeScreenshot] = useState(false);
  const [includeLog, setIncludeLog] = useState(true);
  // The dialog hides itself while the tab capture runs so it doesn't cover
  // the page the screenshot is about.
  const [capturing, setCapturing] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [uploadingLabel, setUploadingLabel] = useState<string | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [uploadWarning, setUploadWarning] = useState<string | null>(null);
  const [submittedId, setSubmittedId] = useState<bigint | null>(null);
  const [submitted, setSubmitted] = useState(false);
  const [tokenPending, setTokenPending] = useState(false);
  // Numbers the token requests: only the latest may act on its result, and
  // closing the dialog (or unmounting) bumps it, so a request still in flight
  // — the open's OR a Retry's — can't dial a socket for a closed dialog.
  const connectSeq = useRef(0);

  const profile = useMemo(
    () => ({
      displayName: user
        ? [user.firstName, user.lastName].filter(Boolean).join(' ') || user.email || ''
        : '',
      email: user?.email || '',
    }),
    [user],
  );

  // A fresh, org-scoped token on every open and Retry: the module files each
  // report under the org of the token the socket was opened with, and
  // connectBugReport rebuilds the socket when that org changed (#99 cause 4).
  const connect = useCallback(
    (retry: boolean) => {
      const seq = (connectSeq.current += 1);
      const isLatest = () => seq === connectSeq.current;
      setTokenPending(true);
      void withTimeout(
        Promise.resolve().then(() => getAccessToken()),
        TOKEN_TIMEOUT_MS,
        'Timed out waiting for a fresh sign-in token.',
      ).then(
        (token) => {
          if (!isLatest()) return; // closed, or superseded by a newer request
          setTokenPending(false);
          const input = { uri: STDB_URI, token: token ?? null, profile };
          if (retry) retryBugReportConnection(input);
          else connectBugReport(input);
        },
        (error: unknown) => {
          if (!isLatest()) return;
          setTokenPending(false);
          console.warn('[bug-report] could not get a sign-in token:', error);
          reportBugReportTokenFailure(
            error instanceof Error ? error.message : 'Could not get a sign-in token.',
          );
        },
      );
    },
    [getAccessToken, profile],
  );

  // Lazy connect: the module socket opens the first time the dialog opens.
  // The cleanup drops whatever token request is still in flight.
  useEffect(() => {
    if (!isOpen) return;
    connect(false);
    return () => {
      connectSeq.current += 1;
    };
  }, [isOpen, connect]);

  // Escape dismisses, like every modal should — but not mid-submit, or the
  // upload-progress and partial-failure feedback vanishes with the dialog
  // (matching the backdrop guard below).
  useEffect(() => {
    if (!isOpen) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !submitting) onClose();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [isOpen, onClose, submitting]);

  // Fresh form each open.
  useEffect(() => {
    if (!isOpen) {
      setKind('Bug');
      setSummary('');
      setDescription('');
      setPendingFiles([]);
      setFileError(null);
      setIncludeScreenshot(false);
      setIncludeLog(true);
      setCapturing(false);
      setUploadingLabel(null);
      setUploadWarning(null);
      setSubmitError(null);
      setSubmittedId(null);
      setSubmitted(false);
      setSubmitting(false);
      setTokenPending(false);
    }
  }, [isOpen]);

  // Client-side validation so a bad pick fails fast with a friendly message
  // instead of a server round-trip; only valid files ever enter the list.
  const handlePickFiles = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      const picked = Array.from(e.target.files ?? []);
      e.target.value = ''; // let the same file be re-picked after a remove
      if (picked.length === 0) return;
      const problems: string[] = [];
      const next = [...pendingFiles];
      for (const file of picked) {
        const problem = validateImageFile(file);
        if (problem) {
          problems.push(problem);
          continue;
        }
        if (next.length >= MAX_REPORT_IMAGES) {
          problems.push(`You can attach up to ${MAX_REPORT_IMAGES} images.`);
          break;
        }
        next.push(file);
      }
      setPendingFiles(next);
      setFileError(problems.length ? problems.join(' ') : null);
    },
    [pendingFiles],
  );

  const removeFile = useCallback((index: number) => {
    setPendingFiles((files) => files.filter((_, i) => i !== index));
    setFileError(null);
  }, []);

  const handleSubmit = useCallback(
    async (e: FormEvent) => {
      e.preventDefault();
      if (!summary.trim() || !description.trim() || submitting) return;
      setSubmitting(true);
      setSubmitError(null);
      setSubmitted(false);
      setUploadWarning(null);

      // Site context is best-effort — a report must still send when the org
      // subscription hasn't delivered any projects yet.
      const site = sites[tweaks.siteIndex] ?? sites[0];

      try {
        // Capture first: getDisplayMedia needs the transient user activation of
        // the Submit click, and the dialog must be hidden for the frame. A
        // declined prompt simply files the report without a screenshot.
        let screenshot: Blob | null = null;
        if (includeScreenshot) {
          setUploadingLabel('Capturing this tab…');
          setCapturing(true);
          await new Promise<void>((r) => requestAnimationFrame(() => requestAnimationFrame(() => r())));
          try {
            screenshot = await captureTabScreenshot();
          } finally {
            setCapturing(false);
          }
        }
        const log = includeLog ? new Blob([getSessionLogText()], { type: 'text/plain' }) : null;

        const input = {
          kind,
          summary,
          description,
          reporterDisplayName: profile.displayName,
          reporterEmail: profile.email,
          reporterOrgName: org.slug,
          reporterProjectName: site?.projectName ?? '',
          reporterProjectId: site?.id ?? '',
          includeScreenshot: screenshot !== null,
          includeLog: log !== null,
        };
        // With anything to attach the id is REQUIRED (the register reducers
        // key on it), so ask the submit helper for the longer capture window.
        const hasAttachments = pendingFiles.length > 0 || screenshot !== null || log !== null;
        setUploadingLabel(null);
        const id = hasAttachments
          ? await submitBugReport(input, IMAGE_ID_WAIT_MS)
          : await submitBugReport(input);
        setSubmittedId(id);

        // The report is filed from here on — attachment trouble is a warning on
        // a successful submit, never a submit failure.
        if (hasAttachments) {
          if (id === null) {
            setUploadWarning(
              'The report was filed, but its id never arrived, so the attachments could not be added.',
            );
          } else {
            const problems: string[] = [];
            try {
              const extras = await uploadReportExtras(id, { screenshot, log }, { onProgress: setUploadingLabel });
              for (const r of extras) {
                if (!r.ok) problems.push(`${r.kind === 'Screenshot' ? 'screenshot' : 'session log'} (${r.error ?? 'unknown error'})`);
              }
            } catch (err) {
              problems.push(`screenshot/log (${err instanceof Error ? err.message : String(err)})`);
            }
            if (pendingFiles.length) {
              try {
                const results = await uploadReportImages(id, pendingFiles, {
                  onProgress: (current, total) =>
                    setUploadingLabel(`Uploading image ${current} of ${total}…`),
                });
                for (const f of results) {
                  if (!f.ok) problems.push(`${f.filename} (${f.error ?? 'unknown error'})`);
                }
              } catch (err) {
                problems.push(`images (${err instanceof Error ? err.message : String(err)})`);
              }
            }
            if (problems.length) {
              setUploadWarning(`The report was filed, but these could not be attached: ${problems.join('; ')}`);
            }
          }
        }

        setSubmitted(true);
        setSummary('');
        setDescription('');
        setKind('Bug');
        setPendingFiles([]);
        setFileError(null);
      } catch (err) {
        setSubmitError(err instanceof Error ? err.message : String(err));
      } finally {
        setUploadingLabel(null);
        setSubmitting(false);
      }
    },
    [
      kind,
      summary,
      description,
      pendingFiles,
      includeScreenshot,
      includeLog,
      submitting,
      profile,
      org.slug,
      sites,
      tweaks.siteIndex,
    ],
  );

  if (!isOpen) return null;

  const { connecting: showConnecting, showError, ready } = bugReportConnectionView({
    status,
    lastError,
    tokenPending,
  });
  const submitReason = firstReason(
    [!ready, 'Connecting to the bug-report service…'],
    [!summary.trim(), 'Add a summary first'],
    [!description.trim(), 'Describe what happened first'],
  );
  const canSubmit = submitReason === null && !submitting;
  const atImageCap = pendingFiles.length >= MAX_REPORT_IMAGES;

  return (
    <div
      onMouseDown={(e) => {
        // Backdrop dismiss — only a press on the overlay itself, so a drag
        // that ends outside the card can't nuke half-typed input.
        if (e.target === e.currentTarget && !submitting) onClose();
      }}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0, 0, 0, 0.55)',
        display: 'grid',
        placeItems: 'center',
        zIndex: 60,
        padding: 16,
        // Out of the frame while the tab capture runs.
        visibility: capturing ? 'hidden' : undefined,
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="bug-report-title"
        style={{
          width: 'min(520px, 100%)',
          maxHeight: 'min(640px, 92vh)',
          display: 'flex',
          flexDirection: 'column',
          background: 'var(--bg-1)',
          border: '1px solid var(--border)',
          borderRadius: 'var(--r-lg, 12px)',
          boxShadow: '0 16px 48px rgba(0, 0, 0, 0.45)',
          color: 'var(--text)',
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            padding: '14px 18px',
            borderBottom: '1px solid var(--border)',
          }}
        >
          <h2 id="bug-report-title" style={{ margin: 0, font: '700 15px var(--sans)' }}>
            Report a problem
          </h2>
          <span style={{ flex: 1 }} />
          <button
            type="button"
            className="icon-btn"
            onClick={onClose}
            aria-label="Close"
            title="Close"
            disabled={submitting}
          >
            <Icon of={Icons.action.remove} size={14} />
          </button>
        </div>
        <form onSubmit={handleSubmit} style={{ display: 'contents' }}>
          <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', padding: '14px 18px', display: 'grid', gap: 14 }}>
            {showConnecting ? (
              <p style={{ margin: 0, color: 'var(--muted)', fontSize: 12.5 }}>
                Connecting to the bug-report service…
              </p>
            ) : null}
            {showError ? (
              <p style={{ margin: 0, color: 'var(--bad)', fontSize: 12.5 }}>
                Could not reach the bug-report service{lastError ? `: ${lastError}` : '.'}{' '}
                <button
                  type="button"
                  onClick={() => connect(true)}
                  style={{ ...BTN_STYLE, padding: '2px 10px', fontSize: 11.5, marginLeft: 6 }}
                >
                  Retry
                </button>
              </p>
            ) : null}

            <div>
              <span style={LABEL_STYLE} id="bug-report-kind-label">
                Kind
              </span>
              <div role="radiogroup" aria-labelledby="bug-report-kind-label" style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                {KIND_OPTIONS.map((opt) => {
                  const on = kind === opt.tag;
                  return (
                    <label
                      key={opt.tag}
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        padding: '5px 12px',
                        borderRadius: 999,
                        border: `1px solid ${on ? 'var(--amber-line)' : 'var(--border-2)'}`,
                        background: on ? 'var(--amber-soft)' : 'var(--panel)',
                        color: on ? 'var(--amber-2)' : 'var(--text-2)',
                        font: '600 12px var(--sans)',
                        cursor: 'pointer',
                      }}
                    >
                      <input
                        type="radio"
                        name="bug-report-kind"
                        value={opt.tag}
                        checked={on}
                        onChange={() => setKind(opt.tag)}
                        style={{ position: 'absolute', opacity: 0, pointerEvents: 'none' }}
                      />
                      {opt.label}
                    </label>
                  );
                })}
              </div>
            </div>

            <div>
              <label style={LABEL_STYLE} htmlFor="bug-report-summary">
                Summary
              </label>
              <input
                id="bug-report-summary"
                style={FIELD_STYLE}
                value={summary}
                onChange={(e) => setSummary(e.target.value)}
                placeholder="One line describing the issue"
                autoFocus
                required
              />
            </div>

            <div>
              <label style={LABEL_STYLE} htmlFor="bug-report-description">
                Description
              </label>
              <textarea
                id="bug-report-description"
                style={{ ...FIELD_STYLE, resize: 'vertical', minHeight: 96 }}
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="What happened, what you expected, and steps to reproduce"
                rows={5}
                required
              />
            </div>

            <div>
              <span style={LABEL_STYLE} id="bug-report-images-label">
                Images
              </span>
              <div style={{ display: 'grid', gap: 6 }} aria-labelledby="bug-report-images-label">
                {pendingFiles.length > 0 ? (
                  <ul
                    aria-label="Attached images"
                    style={{ listStyle: 'none', margin: 0, padding: 0, display: 'grid', gap: 4 }}
                  >
                    {pendingFiles.map((f, i) => (
                      <li
                        key={`${f.name}-${i}`}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 8,
                          padding: '5px 8px',
                          background: 'var(--panel)',
                          border: '1px solid var(--border-2)',
                          borderRadius: 'var(--r-sm, 6px)',
                          fontSize: 12.5,
                          minWidth: 0,
                        }}
                      >
                        <span
                          title={f.name}
                          style={{
                            flex: 1,
                            minWidth: 0,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {f.name}
                        </span>
                        <span style={{ flex: 'none', color: 'var(--muted-2)', fontSize: 11 }}>
                          {formatBytes(f.size)}
                        </span>
                        <button
                          type="button"
                          aria-label={`Remove ${f.name}`}
                          title="Remove"
                          disabled={submitting}
                          onClick={() => removeFile(i)}
                          style={{
                            flex: 'none',
                            border: 'none',
                            background: 'transparent',
                            color: 'var(--muted)',
                            font: '600 12px var(--sans)',
                            cursor: submitting ? 'not-allowed' : 'pointer',
                            padding: '0 2px',
                          }}
                        >
                          <Icon of={Icons.action.remove} size={12} />
                        </button>
                      </li>
                    ))}
                  </ul>
                ) : null}
                {/* Stays mounted at the cap (disabled, like the studio twin)
                    so the affordance doesn't vanish under the user. */}
                <label
                  // The hidden input is what's disabled; the label stays
                  // hoverable so its title can say why (#262).
                  aria-disabled={atImageCap || undefined}
                  title={atImageCap ? `${MAX_REPORT_IMAGES} images is the limit — remove one to add another` : undefined}
                  style={{
                    ...BTN_STYLE,
                    justifySelf: 'start',
                    padding: '5px 12px',
                    fontSize: 12,
                    opacity: submitting || atImageCap ? 0.6 : 1,
                    cursor: submitting || atImageCap ? 'not-allowed' : 'pointer',
                  }}
                >
                  <input
                    type="file"
                    multiple
                    accept=".png,.jpg,.jpeg,.webp,.gif,image/png,image/jpeg,image/webp,image/gif"
                    aria-label="Attach image files"
                    disabled={submitting || atImageCap}
                    onChange={handlePickFiles}
                    style={{ display: 'none' }}
                  />
                  Attach images
                </label>
                <span style={{ color: 'var(--muted-2)', fontSize: 11 }}>
                  Up to {MAX_REPORT_IMAGES} images, 15MB each.
                </span>
                {fileError ? (
                  <span style={{ color: 'var(--warn)', fontSize: 12 }}>{fileError}</span>
                ) : null}
              </div>
            </div>

            <div>
              <span style={LABEL_STYLE} id="bug-report-extras-label">
                Diagnostics
              </span>
              <div style={{ display: 'grid', gap: 6 }} aria-labelledby="bug-report-extras-label">
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12.5, cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={includeScreenshot}
                    disabled={submitting}
                    onChange={(e) => setIncludeScreenshot(e.target.checked)}
                  />
                  Also capture a screenshot of this tab
                </label>
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12.5, cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={includeLog}
                    disabled={submitting}
                    onChange={(e) => setIncludeLog(e.target.checked)}
                  />
                  Include session log
                </label>
                <span style={{ color: 'var(--muted-2)', fontSize: 11 }}>
                  The tab capture asks your browser to share this tab. Screenshots and logs can contain
                  project data and are visible to anyone who can see this report.
                </span>
              </div>
            </div>

            {uploadingLabel ? (
              <p style={{ margin: 0, color: 'var(--muted)', fontSize: 12.5 }}>{uploadingLabel}</p>
            ) : null}
            {submitError ? (
              <p style={{ margin: 0, color: 'var(--bad)', fontSize: 12.5 }}>{submitError}</p>
            ) : null}
            {uploadWarning ? (
              <p style={{ margin: 0, color: 'var(--warn)', fontSize: 12.5 }}>{uploadWarning}</p>
            ) : null}
            {submitted ? (
              <p style={{ margin: 0, color: 'var(--good)', fontSize: 12.5 }}>
                {submittedId !== null ? `Report #${submittedId} submitted` : 'Report submitted'} —
                thank you. The team triages reports in the platform queue.
              </p>
            ) : null}

          </div>
          <div
            style={{
              display: 'flex',
              justifyContent: 'flex-end',
              gap: 8,
              padding: '12px 18px',
              borderTop: '1px solid var(--border)',
            }}
          >
            <button type="button" style={BTN_STYLE} onClick={onClose} disabled={submitting}>
              Cancel
            </button>
            <GatedButton
              type="submit"
              busy={submitting}
              reason={submitReason}
              style={{
                ...BTN_STYLE,
                background: canSubmit ? 'var(--amber)' : 'var(--panel-3)',
                borderColor: canSubmit ? 'var(--amber)' : 'var(--border-2)',
                color: canSubmit ? '#241703' : 'var(--muted)',
                cursor: canSubmit ? 'pointer' : 'not-allowed',
              }}
            >
              {submitting ? 'Submitting…' : 'Submit report'}
            </GatedButton>
          </div>
        </form>
      </div>
    </div>
  );
}
